import type { GuestQuoteLinkParams } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { isQuoteNotFoundError, useGuestQuoteViewQuery } from '@/entities/quote';
import { Button } from '@/shared/ui';
import { GuestQuoteClaimed } from './guest-quote-claimed';
import { GuestQuoteClosed } from './guest-quote-closed';
import { GuestQuoteIssued } from './guest-quote-issued';
import { GuestQuoteNotFound } from './guest-quote-not-found';
import { GuestQuoteReviewing } from './guest-quote-reviewing';

export interface GuestQuoteLinkViewProps {
  /** 주소에서 읽은 링크 — 형식이 틀리면 null 이고, 요청 없이 찾을 수 없음을 보여 준다. */
  readonly params: GuestQuoteLinkParams | null;
}

/**
 * 견적 링크 화면(결정 G9) — 로그인 없이 상태별로 보여 준다. 검토 중(요약·회신 기한), 발행됨(견적서·인쇄·[주문하기]),
 * 끝남(반려·취소·만료·거절 안내), 연결됨(내용 없이 안내)이 있다. 서명 불일치·없는 견적은 하나의 찾을 수 없음이다.
 * 일시 실패는 다시 시도를 준다.
 */
export const GuestQuoteLinkView = ({ params }: GuestQuoteLinkViewProps): ReactElement => {
  const query = useGuestQuoteViewQuery(params);

  if (params === null || isQuoteNotFoundError(query.error?.data?.code)) {
    return <GuestQuoteNotFound />;
  }

  if (query.isPending) {
    return (
      <div>
        <p role="status" className="sr-only">
          견적을 불러오는 중입니다.
        </p>
        <div aria-hidden className="flex flex-col gap-10">
          <div className="h-24 w-full max-w-md animate-pulse bg-muted/70" />
          <div className="min-h-96 animate-pulse bg-muted/70" />
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center"
      >
        <p className="text-sm text-destructive">견적을 불러오지 못했어요.</p>
        <Button
          variant="outline"
          className="h-11 border-foreground/20 px-6"
          onClick={() => {
            void query.refetch();
          }}
        >
          다시 시도
        </Button>
      </div>
    );
  }

  const view = query.data;
  switch (view.state) {
    case 'REVIEWING':
      return <GuestQuoteReviewing view={view} />;
    case 'ISSUED':
      return <GuestQuoteIssued view={view} params={params} />;
    case 'CLOSED':
      return <GuestQuoteClosed view={view} />;
    case 'CLAIMED':
      return <GuestQuoteClaimed quoteRequestId={params.quoteRequestId} />;
  }
};
